import React, { useEffect, useState } from 'react';
import { Save, BookOpen, Mail, AlertCircle, CheckCircle2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const Field = ({ label, hint, children }) => (
  <div className="space-y-1.5">
    <label className="block text-sm font-medium text-gray-700">{label}</label>
    {children}
    {hint && <p className="text-xs text-gray-500">{hint}</p>}
  </div>
);

const CataloguePdfTab = () => {
  const [form, setForm] = useState({
    catalogueUrlWithPrices: '',
    catalogueUrlWithoutPrices: '',
    catalogueEmailSubject: '',
    catalogueEmailBody: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null); // {type, msg}

  useEffect(() => {
    fetch(`${API}/api/site-settings`)
      .then((r) => r.json())
      .then((data) => {
        setForm({
          catalogueUrlWithPrices: data.catalogueUrlWithPrices || '',
          catalogueUrlWithoutPrices: data.catalogueUrlWithoutPrices || '',
          catalogueEmailSubject: data.catalogueEmailSubject || '',
          catalogueEmailBody: data.catalogueEmailBody || '',
        });
      })
      .catch(() => setStatus({ type: 'error', msg: 'Impossible de charger les paramètres.' }))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setStatus(null);
    try {
      const res = await fetch(`${API}/api/site-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        setStatus({ type: 'success', msg: 'Modifications enregistrées avec succès.' });
      } else {
        setStatus({ type: 'error', msg: 'Erreur lors de la sauvegarde.' });
      }
    } catch (_) {
      setStatus({ type: 'error', msg: 'Erreur réseau, réessayez.' });
    } finally {
      setSaving(false);
    }
  };

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  if (loading) {
    return <div className="text-gray-500">Chargement des paramètres…</div>;
  }

  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Catalogue PDF</h2>
          <p className="text-sm text-gray-500 mt-1">
            Liens du catalogue et email envoyé aux prospects.
          </p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-5 py-2.5 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors font-medium disabled:opacity-50"
          data-testid="catalogue-pdf-save-btn"
        >
          <Save className="w-4 h-4" /> {saving ? 'Enregistrement…' : 'Enregistrer'}
        </button>
      </div>

      {status && (
        <div
          className={`flex items-center gap-2 px-4 py-3 rounded-lg mb-6 ${
            status.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
          }`}
          data-testid="catalogue-pdf-status"
        >
          {status.type === 'success' ? <CheckCircle2 className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
          <span className="text-sm">{status.msg}</span>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-center gap-2 mb-5">
          <BookOpen className="w-5 h-5 text-orange-500" />
          <h3 className="text-lg font-semibold text-gray-900">Liens du catalogue (Heyzine)</h3>
        </div>
        <div className="space-y-5">
          <Field
            label="URL catalogue AVEC prix"
            hint="Lien envoyé par email aux prospects qui ont rempli le formulaire (téléchargement)."
          >
            <input
              type="url"
              value={form.catalogueUrlWithPrices}
              onChange={update('catalogueUrlWithPrices')}
              placeholder="https://heyzine.com/flip-book/..."
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:outline-none text-sm"
              data-testid="catalogue-url-with-prices"
            />
          </Field>
          <Field
            label="URL catalogue SANS prix"
            hint='Lien public utilisé par les boutons "Feuilleter le catalogue" (Hero, Footer).'
          >
            <input
              type="url"
              value={form.catalogueUrlWithoutPrices}
              onChange={update('catalogueUrlWithoutPrices')}
              placeholder="https://heyzine.com/flip-book/..."
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:outline-none text-sm"
              data-testid="catalogue-url-without-prices"
            />
          </Field>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-2">
          <Mail className="w-5 h-5 text-orange-500" />
          <h3 className="text-lg font-semibold text-gray-900">Email envoyé au client</h3>
        </div>
        <p className="text-xs text-gray-500 mb-5">
          Placeholders disponibles : <code className="bg-gray-100 px-1.5 py-0.5 rounded">{'{firstName}'}</code>{' '}
          (prénom du prospect),{' '}
          <code className="bg-gray-100 px-1.5 py-0.5 rounded">{'{catalogueLink}'}</code> (URL catalogue avec prix
          ci-dessus).
        </p>
        <div className="space-y-5">
          <Field label="Sujet de l'email">
            <input
              type="text"
              value={form.catalogueEmailSubject}
              onChange={update('catalogueEmailSubject')}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:outline-none text-sm"
              data-testid="catalogue-email-subject"
            />
          </Field>
          <Field label="Corps de l'email">
            <textarea
              rows={10}
              value={form.catalogueEmailBody}
              onChange={update('catalogueEmailBody')}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:outline-none text-sm font-mono"
              data-testid="catalogue-email-body"
            />
          </Field>
        </div>
        <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-800">
            <strong>Configuration requise :</strong> pour activer l'envoi, fournissez <code>RESEND_API_KEY</code>{' '}
            et <code>SENDER_EMAIL</code> au backend. Sans ces clés, la demande est enregistrée mais l'email n'est
            pas envoyé (un log apparaît côté serveur).
          </p>
        </div>
      </div>
    </>
  );
};

export default CataloguePdfTab;
